import './global.css';
import { MantineProvider } from '@mantine/core';
import '@mantine/charts/styles.css';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import { Notifications } from '@mantine/notifications';
import '@mantine/notifications/styles.css';
import { ErrorBoundary } from '@sentry/react';
import 'mantine-datatable/styles.layer.css';
import { BrowserRouter } from 'react-router';
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import AllRoutes from './AllRoutes';
import { createMantineTheme, cssVariableResolver } from './UI/theme/theme';
import { GenericErrorPage } from './error/GenericErrorPage';
import { GlobalModalProvider, useGlobalModal } from 'src/error/GlobalModalProvider';
import { ApiError, ApiErrorCode } from './error/ApiError';
import { FeatureFlagProvider } from './feature-flags/FeatureFlagProvider';
import { defaultQueryFn } from 'src/common/utils/api';
import { PageViewTracker } from './tracking/PageViewTracker';
import { SessionExpirationMonitor } from './auth/SessionExpirationMonitor';
import { MaintenanceGate } from './maintenance/MaintenanceGate';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useEffect } from 'react';
import { SharePointProvider } from 'src/data-capture/file-management/drop-zone/SharePointProvider';

dayjs.extend(customParseFormat);

const queryClient = new QueryClient({
  mutationCache: new MutationCache(),
  defaultOptions: {
    queries: {
      retry: (failureCount: number, error: unknown) => {
        if (error instanceof Response) {
          if (error.status === 401 || error.status === 403) {
            return false;
          }
        }

        return failureCount < 3;
      },
      queryFn: defaultQueryFn,
    },
  },
});

function AppContent() {
  const { showError, showWarning } = useGlobalModal();

  useEffect(() => {
    const handleUiWarning = (event: CustomEvent<string>) => {
      showWarning(event.detail);
    };
    window.addEventListener('afternoon-ui-warning', handleUiWarning as EventListener);

    queryClient.getMutationCache().config.onError = (error: Error) => {
      if (error instanceof ApiError && error.code === ApiErrorCode.ENTITY_HAS_DEPENDENCIES) {
        showError(error.message);
      }

      if (ApiError.isSpreadsheetTooLargeError(error)) {
        showError(error.message);
      }
    };

    return () => {
      window.removeEventListener('afternoon-ui-warning', handleUiWarning as EventListener);
      queryClient.getMutationCache().config.onError = undefined;
    };
  }, [showError, showWarning]);

  return (
    <BrowserRouter>
      <ErrorBoundary fallback={({ error }) => <GenericErrorPage error={error} />}>
        <QueryClientProvider client={queryClient}>
          <PageViewTracker />
          <SessionExpirationMonitor />
          <SharePointProvider>
            <MaintenanceGate>
              <AllRoutes />
            </MaintenanceGate>
          </SharePointProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

function App() {
  const theme = createMantineTheme();

  return (
    <MantineProvider theme={theme} cssVariablesResolver={cssVariableResolver}>
      <Notifications />
      <GlobalModalProvider>
        <AppContent />
      </GlobalModalProvider>
    </MantineProvider>
  );
}

export default function AppWithFlags() {
  return (
    <FeatureFlagProvider>
      <App />
    </FeatureFlagProvider>
  );
}
